/* =====================================================================
   ENTROPICA — THE INDEX (Loyalty & Referral) CLIENT ENGINE

   Ledger provider is headless. All customer state is rendered SERVER-SIDE
   from customer metafields into window.EntropicaIndex by
   snippets/index-state.liquid, so the UI never flashes and never waits on
   a third-party script. This file only handles:
     1. referral link capture + attribution cookie
     2. copy / share affordances
     3. cart earn-preview (anticipated gain at the decision point)
     4. the Gate (email capture) — open rules, suppression, submit
     5. optional provider sync ping (fire-and-forget, never blocking)

   Swapping Rivo <-> Smile <-> fully-custom = change PROVIDER below and the
   metafield namespace in snippets/index-state.liquid. Nothing else moves.
   ===================================================================== */

(function () {
  'use strict';

  // Boot guard: snippets/index-state.liquid may render more than once per page
  // (theme.liquid plus a defensive render inside each Index section). Binding
  // the listeners twice would double-fire the Gate and the cart preview.
  if (window.__eidxBooted) return;
  window.__eidxBooted = true;

  var CFG = (window.EntropicaIndex = window.EntropicaIndex || {});

  // Defaults; snippets/index-state.liquid overwrites these server-side.
  CFG.provider = CFG.provider || 'custom';
  CFG.joulesPerDollar = CFG.joulesPerDollar || 10; // 10 J per $1 = 10% issued
  CFG.argonJoulesPerDollar = CFG.argonJoulesPerDollar || 25; // 2.5x — vial margin is 61-71%
  CFG.argonProductIds = CFG.argonProductIds || [];
  CFG.joulesPerUnitCurrency = CFG.joulesPerUnitCurrency || 100; // 100 J = $1.00
  CFG.redeemCapPct = CFG.redeemCapPct || 0.10; // burn <= 10% of subtotal
  CFG.balance = CFG.balance || 0;
  CFG.tierMultiplier = CFG.tierMultiplier || 1;
  CFG.loggedIn = !!CFG.loggedIn;
  CFG.moneyFormat = CFG.moneyFormat || '${{amount}}';

  var REF_COOKIE = 'eidx_ref';
  var GATE_COOKIE = 'eidx_gate';
  var REF_TTL_DAYS = 60;
  var GATE_DISMISS_DAYS = 14;

  /* ---------------------------------------------------------------
     Cookies
     --------------------------------------------------------------- */
  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    document.cookie =
      name + '=' + encodeURIComponent(value) + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }

  function getCookie(name) {
    var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return m ? decodeURIComponent(m.pop()) : '';
  }

  /* ---------------------------------------------------------------
     Money / Joules formatting
     --------------------------------------------------------------- */
  function joulesToMoney(joules) {
    return joules / CFG.joulesPerUnitCurrency;
  }

  function formatMoney(amount) {
    return '$' + amount.toFixed(2);
  }

  function formatJoules(joules) {
    return Math.floor(joules).toLocaleString('en-US');
  }

  /* ---------------------------------------------------------------
     1. REFERRAL ATTRIBUTION
     A ?ref= param is stamped to a 60-day cookie and mirrored into a cart
     attribute, so the referrer is still credited if the referee converts
     weeks later on a different session.
     --------------------------------------------------------------- */
  function captureReferral() {
    var code = new URLSearchParams(window.location.search).get('ref');
    if (!code) return;

    code = code.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 40);
    if (!code) return;

    setCookie(REF_COOKIE, code, REF_TTL_DAYS);
    stampCartAttribute(code);
  }

  function stampCartAttribute(code) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ attributes: { 'Index Referrer': code } })
    }).catch(function () {
      /* non-blocking: attribution also lives in the cookie */
    });
  }

  /* ---------------------------------------------------------------
     2. COPY + SHARE
     --------------------------------------------------------------- */
  function bindCopy() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-eidx-copy]');
      if (!btn) return;

      e.preventDefault();
      var field = document.querySelector(btn.getAttribute('data-eidx-copy'));
      if (!field) return;

      var done = function () {
        var original = btn.getAttribute('data-label-idle') || btn.textContent.trim();
        btn.setAttribute('data-label-idle', original);
        btn.textContent = btn.getAttribute('data-label-done') || 'Copied';
        btn.classList.add('is-copied');
        setTimeout(function () {
          btn.textContent = original;
          btn.classList.remove('is-copied');
        }, 2200);
      };

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(field.value).then(done).catch(function () {
          legacyCopy(field);
          done();
        });
      } else {
        legacyCopy(field);
        done();
      }
    });
  }

  /* ---------------------------------------------------------------
     2a. NATIVE SHARE

     navigator.share() opens the operating system's own sheet, which lists
     every app the member has installed — Instagram, Discord, Signal, whatever
     — none of which expose a web sharing API of their own. It is the only way
     to reach those apps from a browser, and it costs one button.

     Requires a user gesture and a secure context, and it rejects when the
     member simply dismisses the sheet, which is not an error and must not be
     surfaced as one.

     The button ships hidden and is revealed only where the API exists, so
     desktop never sees a control that would do nothing.
     --------------------------------------------------------------- */
  function bindShare() {
    var buttons = document.querySelectorAll('[data-eidx-share]');
    if (!buttons.length || !navigator.share) return;

    Array.prototype.forEach.call(buttons, function (b) {
      b.hidden = false;
    });

    // The sheet already covers mail, WhatsApp and SMS. Leaving those buttons
    // alongside it is three ways to do one thing.
    Array.prototype.forEach.call(document.querySelectorAll('.eidx-share__fallback'), function (a) {
      a.hidden = true;
    });

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-eidx-share]');
      if (!btn) return;

      e.preventDefault();
      navigator
        .share({
          title: btn.getAttribute('data-share-title') || '',
          text: btn.getAttribute('data-share-text') || '',
          url: btn.getAttribute('data-share-url') || window.location.href
        })
        .catch(function () {
          /* dismissed, or the target app refused it — neither is a failure */
        });
    });
  }

  function legacyCopy(field) {
    field.removeAttribute('readonly');
    field.select();
    field.setSelectionRange(0, 99999);
    try {
      document.execCommand('copy');
    } catch (err) {
      /* clipboard unavailable — the field is selected for manual copy */
    }
    field.setAttribute('readonly', 'readonly');
  }

  /* ---------------------------------------------------------------
     2b. THE FULL RECORD
     Every allocation is rendered server-side; the ones past the section's
     display limit ship with the `hidden` attribute. This only unhides them,
     so the record never has to hand a member off to Shopify's order index
     just to see an older order.
     --------------------------------------------------------------- */
  function bindOrdersToggle() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-eidx-orders-more]');
      if (!btn) return;

      e.preventDefault();

      var rows = document.querySelectorAll('.eidx-order--overflow');
      if (!rows.length) return;

      var expanding = rows[0].hidden;

      Array.prototype.forEach.call(rows, function (row) {
        row.hidden = !expanding;
        // Collapsing a row that the member had opened would otherwise leave
        // it open the next time they expand the record.
        if (!expanding) row.open = false;
      });

      btn.textContent = expanding
        ? btn.getAttribute('data-label-less') || 'Show less'
        : btn.getAttribute('data-label-more') || 'Show the full record';

      // Row count changed, so the masked block's geometry did too.
      window.dispatchEvent(new Event('resize'));
    });
  }

  /* ---------------------------------------------------------------
     2c. AUTOMATIC REDEMPTION

     The member never sees or types a code. On checkout we ask the ledger app
     (through a Shopify app proxy) to mint a single-use, customer-locked
     discount for exactly the capped amount, then hand it to checkout via
     ?discount=. The discount is still ITEMISED at checkout — "silent" here
     means no manual step, not hidden. A balance that drains invisibly is a
     support ticket.

     Two hard rules in here:
       - this must NEVER block a sale. Any failure, any slow response, and we
         let the normal checkout proceed untouched.
       - it only fires when a redemption is actually possible, so we do not
         round-trip on every checkout click for members holding nothing.
     --------------------------------------------------------------- */
  var REDEEM_ENDPOINT = '/apps/index/redeem';
  var REDEEM_TIMEOUT_MS = 2500;
  var REDEEM_OFF_KEY = 'eidx_redeem_off';
  var redeeming = false;

  /* The opt-out. Absent means ON — the default is to apply, and only an
     explicit choice turns it off. Wrapped because localStorage throws outright
     in Safari private mode, and a storage failure must mean "use the default",
     never "break checkout". */
  function redeemOff() {
    try {
      return localStorage.getItem(REDEEM_OFF_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function setRedeemOff(off) {
    try {
      if (off) localStorage.setItem(REDEEM_OFF_KEY, '1');
      else localStorage.removeItem(REDEEM_OFF_KEY);
    } catch (e) {
      /* private mode: the choice lasts for this page only */
    }
  }

  function syncRedeemToggles() {
    var rows = document.querySelectorAll('[data-eidx-redeem-row]');
    if (!rows.length) return;
    var off = redeemOff();

    Array.prototype.forEach.call(rows, function (row) {
      var box = row.querySelector('[data-eidx-redeem-toggle]');
      var label = row.querySelector('[data-eidx-redeem-label]');
      if (box) box.checked = !off;
      // Always name the CURRENT state. A switch that only says what it does
      // when you flip it is a switch you have to flip to understand.
      if (label) label.textContent = off ? 'Saving your Joules for later' : 'Applying at checkout';
    });
  }

  function bindRedeemToggle() {
    document.addEventListener('change', function (e) {
      var box = e.target.closest && e.target.closest('[data-eidx-redeem-toggle]');
      if (!box) return;
      setRedeemOff(!box.checked);
      syncRedeemToggles();
    });
  }

  function bindCheckoutRedeem() {
    if (!CFG.loggedIn) return;

    document.addEventListener(
      'click',
      function (e) {
        var btn = e.target.closest(
          'button[name="checkout"], input[name="checkout"], a[href^="/checkout"]'
        );
        if (!btn || redeeming) return;

        // Cheapest possible bail-out: below one $5 step nothing can apply.
        if (!CFG.balance || CFG.balance < 500) return;

        // The member asked to keep their holding. Let checkout proceed
        // untouched — same path as a failure, minus the round trip.
        if (redeemOff()) return;

        e.preventDefault();
        redeeming = true;

        var done = false;
        var proceed = function (url) {
          if (done) return;
          done = true;
          redeeming = false;
          window.location.href = url || '/checkout';
        };

        // Hard ceiling. If the app is slow or down, the member still checks out.
        setTimeout(function () { proceed('/checkout'); }, REDEEM_TIMEOUT_MS);

        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        fetch(root + 'cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            return fetch(REDEEM_ENDPOINT, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ subtotalCents: cart.items_subtotal_price })
            });
          })
          .then(function (r) { return r.json(); })
          .then(function (out) {
            proceed(
              out && out.applicable && out.code
                ? '/checkout?discount=' + encodeURIComponent(out.code)
                : '/checkout'
            );
          })
          .catch(function () { proceed('/checkout'); });
      },
      true // capture, so this runs before entropica-core.js's cart handler
    );
  }

  /* ---------------------------------------------------------------
     3. CART EARN PREVIEW
     Renders "this order yields N J" and, when a balance exists, how much
     of it this cart can absorb. Recalculates on every cart mutation.
     --------------------------------------------------------------- */
  function renderCartPreview() {
    var nodes = document.querySelectorAll('[data-eidx-cart-preview]');
    if (!nodes.length) return;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root + 'cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var subtotal = cart.items_subtotal_price / 100;
        if (subtotal <= 0) {
          nodes.forEach(function (n) {
            n.hidden = true;
          });
          return;
        }

        // Split the cart so Argon lines yield at their own rate. /cart.js has
        // no tags, so classification runs off the product IDs Liquid emitted.
        var argonIds = CFG.argonProductIds.map(String);
        var argonMoney = 0;
        (cart.items || []).forEach(function (item) {
          if (argonIds.indexOf(String(item.product_id)) !== -1) {
            argonMoney += item.final_line_price / 100;
          }
        });
        var standardMoney = Math.max(0, subtotal - argonMoney);

        var earn = Math.floor(
          (standardMoney * CFG.joulesPerDollar + argonMoney * CFG.argonJoulesPerDollar) *
            CFG.tierMultiplier
        );
        var capMoney = subtotal * CFG.redeemCapPct;
        var burnable = Math.min(CFG.balance, capMoney * CFG.joulesPerUnitCurrency);

        nodes.forEach(function (n) {
          n.hidden = false;

          var earnEl = n.querySelector('[data-eidx-earn]');
          if (earnEl) earnEl.textContent = formatJoules(earn) + ' J';

          var burnEl = n.querySelector('[data-eidx-burnable]');
          if (burnEl) {
            var burnRow = burnEl.closest('[data-eidx-burn-row]');
            var canBurn = CFG.loggedIn && burnable >= CFG.joulesPerUnitCurrency;
            if (canBurn) burnEl.textContent = formatMoney(joulesToMoney(burnable));
            if (burnRow) burnRow.hidden = !canBurn;
          }

          // Free-shipping gap nudge — $75 threshold, per pricing engine.
          var gapEl = n.querySelector('[data-eidx-ship-gap]');
          if (gapEl) {
            var gap = 75 - subtotal;
            if (gap > 0) {
              gapEl.textContent =
                'Add ' + formatMoney(gap) + ' to clear complimentary shipping and yield ' +
                formatJoules(Math.floor(gap * CFG.joulesPerDollar * CFG.tierMultiplier)) + ' J more.';
              gapEl.hidden = false;
            } else {
              gapEl.hidden = true;
            }
          }
        });

        // The drawer replaces this markup wholesale on every cart change, so
        // a fresh checkbox arrives at its default and would misreport a
        // member who had opted out.
        syncRedeemToggles();
      })
      .catch(function () {
        /* leave server-rendered fallback in place */
      });
  }

  /* ---------------------------------------------------------------
     4. THE GATE
     Opens on the earlier of: scroll depth, dwell time, or exit intent.
     Never opens for a subscriber, a logged-in member, or anyone who
     dismissed it inside the suppression window.
     --------------------------------------------------------------- */
  function initGate() {
    var gate = document.querySelector('[data-eidx-gate]');
    if (!gate) return;

    if (CFG.loggedIn || getCookie(GATE_COOKIE)) return;

    var opts = {
      scrollPct: parseInt(gate.getAttribute('data-scroll-pct') || '35', 10),
      dwellMs: parseInt(gate.getAttribute('data-dwell-ms') || '18000', 10),
      exitIntent: gate.getAttribute('data-exit-intent') !== 'false'
    };

    var opened = false;

    function open() {
      if (opened) return;
      opened = true;
      gate.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      var input = gate.querySelector('input[type="email"]');
      if (input) setTimeout(function () { input.focus(); }, 620);
      teardown();
    }

    function close(suppressDays) {
      gate.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      setCookie(GATE_COOKIE, '1', suppressDays || GATE_DISMISS_DAYS);
    }

    function onScroll() {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      if (h > 0 && (window.scrollY / h) * 100 >= opts.scrollPct) open();
    }

    function onExit(e) {
      if (e.clientY <= 0) open();
    }

    function teardown() {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onExit);
      clearTimeout(dwellTimer);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    if (opts.exitIntent && window.matchMedia('(min-width: 768px)').matches) {
      document.addEventListener('mouseout', onExit);
    }
    var dwellTimer = setTimeout(open, opts.dwellMs);

    gate.addEventListener('click', function (e) {
      if (e.target.closest('[data-eidx-gate-close]')) {
        e.preventDefault();
        close(GATE_DISMISS_DAYS);
      } else if (e.target === gate) {
        close(GATE_DISMISS_DAYS);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && gate.classList.contains('is-open')) close(GATE_DISMISS_DAYS);
    });

    // Shopify's customer form posts natively; on success it returns with
    // ?customer_posted=true — suppress for a year and show the claimed state.
    if (new URLSearchParams(window.location.search).get('customer_posted') === 'true') {
      gate.classList.add('is-open', 'is-claimed');
      document.body.classList.add('overflow-hidden');
      setCookie(GATE_COOKIE, '1', 365);
    }

    // Any explicit trigger elsewhere on the page reopens it on demand.
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-eidx-gate-open]')) {
        e.preventDefault();
        opened = false;
        open();
      }
    });
  }

  /* ---------------------------------------------------------------
     Boot
     --------------------------------------------------------------- */
  /* ---------------------------------------------------------------
     0. HEADER OFFSET
     The header is position:fixed and nothing reserves its height, so
     entropica-overrides.css pads #MainContent by --eidx-header-h. The bar is
     not a constant height across templates (96px on some, 109px on others,
     and it changes on resize), so measure it rather than guessing.
     --------------------------------------------------------------- */
  function syncHeaderOffset() {
    var bar = document.querySelector('.header-wrapper') || document.querySelector('sticky-header');
    if (!bar) return;
    var h = Math.round(bar.getBoundingClientRect().height);
    if (h > 0) document.documentElement.style.setProperty('--eidx-header-h', h + 'px');
  }

  /* ---------------------------------------------------------------
     0b. THE FORCEFIELD ENGINE — header physics, scoped to .eidx

     Every other Entropica surface — the homepage, The Curator's Ledger,
     roaster allocations, every product template — masks its own content
     away at the header line as it scrolls, so text dissolves INTO the bar
     instead of running through it. That mask is what makes the fixed
     transparent header read as a header at all.

     The Index pages shipped without it. That is the whole reason
     /pages/entropist and /pages/the-index feel wrong next to the homepage:
     the header is identical, the content behaviour underneath it is not.

     Same engine as sections/homepage-test.liquid, with two deliberate
     differences:

       1. The header line is MEASURED, not hardcoded. The older sections
          use a literal 90. The bar is 96px on some templates and 109px on
          others, so a hardcoded 90 leaves a live band of text sitting
          inside the header. --eidx-header-h is already measured above.

       2. The selector is descendant-scoped to .eidx. The other engines
          each query '.vaporize-target' globally; this one cannot reach a
          homepage element even if the two ever share a template.

     Desktop only. Under 769px the header is frosted glass and separates
     itself, and mask-image is expensive on mobile GPUs (CLAUDE.md 4.3) —
     which is exactly how every other section handles it.
     --------------------------------------------------------------- */
  var FEATHER = 60; // px of gradient. Matches The Curator's Ledger: long
                    // enough to dissolve, short enough not to pre-blur.

  function initForcefield() {
    var targets = document.querySelectorAll('.eidx .vaporize-target');
    if (!targets.length) return;

    var desktop = window.matchMedia('(min-width: 769px)');
    var ticking = false;

    function headerLine() {
      var v = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--eidx-header-h'),
        10
      );
      return v > 0 ? v : 96;
    }

    function clear(el) {
      if (el.style.maskImage || el.style.webkitMaskImage) {
        el.style.webkitMaskImage = '';
        el.style.maskImage = '';
      }
    }

    function frame() {
      ticking = false;

      if (!desktop.matches) {
        Array.prototype.forEach.call(targets, clear);
        return;
      }

      var line = headerLine();

      Array.prototype.forEach.call(targets, function (el) {
        var rect = el.getBoundingClientRect();

        if (rect.top < line + FEATHER && rect.bottom > 0) {
          var clip = line - rect.top;
          var grad =
            'linear-gradient(to bottom, transparent ' + clip + 'px, black ' + (clip + FEATHER) + 'px)';
          el.style.webkitMaskImage = grad;
          el.style.maskImage = grad;
        } else {
          clear(el);
        }
      });
    }

    function schedule() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(frame);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    if (desktop.addEventListener) desktop.addEventListener('change', schedule);

    // Mask correctly on a restored or deep-linked scroll position, before
    // the visitor has scrolled at all.
    frame();
  }

  function boot() {
    syncHeaderOffset();
    window.addEventListener('resize', syncHeaderOffset, { passive: true });
    initForcefield();
    captureReferral();
    bindCopy();
    bindShare();
    bindOrdersToggle();
    bindCheckoutRedeem();
    bindRedeemToggle();
    syncRedeemToggles();
    initGate();
    renderCartPreview();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Recompute the preview whenever the cart changes (Dawn + entropica-core).
  document.addEventListener('cart:updated', renderCartPreview);
  document.documentElement.addEventListener('cart:updated', renderCartPreview);
  if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, renderCartPreview);
  }

  CFG.refreshPreview = renderCartPreview;
})();
